import React from 'react';

/**
 * Kobu editorial design kit — monochrome travel-gazette primitives.
 * Parchment canvas, Gill Sans editorial type, Fira Mono museum-tag labels,
 * hairline obsidian borders, zero shadows, no accent color. Photography is
 * the only color. See the Kobu style reference.
 */

// ─── Museum-tag label ───────────────────────────────────────────────
export const KobuLabel: React.FC<{ children: React.ReactNode; className?: string }> = ({ children, className = '' }) => (
  <span className={`kobu-label text-obsidian ${className}`}>{children}</span>
);

// ─── Hairline badge (FEATURED / NEW) ────────────────────────────────
export const KobuBadge: React.FC<{ children: React.ReactNode; dot?: boolean; className?: string }> = ({ children, dot = true, className = '' }) => (
  <span className={`kobu-badge inline-flex items-center gap-1.5 ${className}`}>
    {dot && <span aria-hidden="true">·</span>}
    {children}
  </span>
);

// ─── Section heading (Gill Sans 33px + optional graphite intro) ──────
export const KobuSectionHeading: React.FC<{ title: string; intro?: string; className?: string }> = ({ title, intro, className = '' }) => (
  <div className={className}>
    <h2 className="kobu-heading text-[28px] sm:text-[33px]">{title}</h2>
    {intro && <p className="kobu-body text-[15px] sm:text-[16px] mt-4 max-w-[480px]">{intro}</p>}
  </div>
);

// ─── View-all overflow link (Gill Sans 21px + ↗) ────────────────────
export const KobuViewAll: React.FC<{ href: string; children: React.ReactNode; className?: string }> = ({ href, children, className = '' }) => (
  <a href={href} className={`kobu-heading text-[18px] sm:text-[21px] text-obsidian hover:text-ash-gray transition-colors inline-flex items-center gap-2 ${className}`}>
    {children} <span aria-hidden="true">↗</span>
  </a>
);

// ─── Property / collection card (image-first, hairline, no shadow) ───
export const KobuPropertyCard: React.FC<{
  image: string;
  title: string;
  location?: string;
  price?: string;
  href?: string;
  badge?: string;
  className?: string;
}> = ({ image, title, location, price, href, badge, className = '' }) => {
  const inner = (
    <>
      <div className="relative">
        <img src={image} alt={title} loading="lazy" className="w-full aspect-[4/3] object-cover bg-gallery-white" />
        {badge && <div className="absolute top-[10px] left-[10px]"><KobuBadge>{badge}</KobuBadge></div>}
      </div>
      <div className="flex items-baseline justify-between gap-4 mt-[13px]">
        <div className="min-w-0">
          <h3 className="kobu-heading font-medium text-[18px] sm:text-[21px] truncate" style={{ fontWeight: 500 }}>{title}</h3>
          {location && <p className="kobu-label text-ash-gray mt-[8px]">{location}</p>}
        </div>
        {price && <span className="kobu-caption text-obsidian shrink-0 tracking-[0.071em]">{price}</span>}
      </div>
    </>
  );
  return href
    ? <a href={href} className={`block group ${className}`}>{inner}</a>
    : <div className={className}>{inner}</div>;
};

// ─── Back-to-top floating control ───────────────────────────────────
export const KobuBackToTop: React.FC = () => {
  const [show, setShow] = React.useState(false);
  React.useEffect(() => {
    const onScroll = () => setShow(window.scrollY > 600);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);
  if (!show) return null;
  return (
    <button
      onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
      aria-label="Back to top"
      className="fixed bottom-6 right-6 z-50 w-10 h-10 rounded-full bg-gallery-white border border-obsidian flex items-center justify-center hover:bg-parchment transition-colors no-print"
    >
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className="text-obsidian">
        <path strokeLinecap="round" strokeLinejoin="round" d="M5 15l7-7 7 7" />
      </svg>
    </button>
  );
};
